import { Component } from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  predict, scalePercent, PredictorResult, SchoolPrediction,
} from './csse-predictor.engine';


@Component({
  selector: 'app-csse-predictor',
  imports: [CommonModule, FormsModule],
  templateUrl: './csse-predictor.component.html',
  styleUrl: './csse-predictor.component.css',
})
export class CssePredictorComponent {
  reading = 0;
  writing = 0;
  maths = 0;

  day = 1;
  month = 9;
  year = new Date().getFullYear() - 10;

  showResult = false;
  result: PredictorResult | null = null;

  readonly days = Array.from({ length: 31 }, (_, i) => i + 1);
  readonly months = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'];
  readonly years = (() => {
    const now = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => now - 13 + i); // Year 5–6 age range
  })();

  readonly subjects = [
    { key: 'reading' as const, label: 'English reading', max: 40 },
    { key: 'writing' as const, label: 'Creative writing', max: 20 },
    { key: 'maths' as const, label: 'Maths', max: 60 },
  ];

  /** Recomputed on every input change for the live readout. */
  get live(): PredictorResult | null {
    return predict({
      reading: this.reading, writing: this.writing, maths: this.maths,
      day: +this.day, month: +this.month, year: +this.year,
    });
  }

  value(key: 'reading' | 'writing' | 'maths'): number {
    return this[key];
  }

  setValue(key: 'reading' | 'writing' | 'maths', raw: number | string, max: number): void {
    const n = Math.round(Number(raw));
    this[key] = Number.isFinite(n) ? Math.min(max, Math.max(0, n)) : 0;
  }

  /** Native input handler — works for both the slider and the number box. */
  onInput(key: 'reading' | 'writing' | 'maths', e: Event, max: number): void {
    const el = e.target as HTMLInputElement;
    this.setValue(key, el.value, max);
    // Typing "75" into a /60 box: snap the box back to the clamped value.
    if (el.type === 'number' && e.type === 'blur') el.value = String(this[key]);
  }

  /**
   * Custom slider (not <input type="range">) so site-wide input styles
   * can't break it. Pointer events cover mouse, touch and pen.
   */
  dragStart(key: 'reading' | 'writing' | 'maths', max: number, e: PointerEvent): void {
    const el = e.currentTarget as HTMLElement;
    e.preventDefault();
    el.focus();
    el.setPointerCapture(e.pointerId);

    const update = (ev: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width));
      this.setValue(key, ratio * max, max);
    };
    const stop = (ev: PointerEvent) => {
      el.releasePointerCapture(ev.pointerId);
      el.removeEventListener('pointermove', update);
      el.removeEventListener('pointerup', stop);
      el.removeEventListener('pointercancel', stop);
    };

    update(e);
    el.addEventListener('pointermove', update);
    el.addEventListener('pointerup', stop);
    el.addEventListener('pointercancel', stop);
  }

  onKey(key: 'reading' | 'writing' | 'maths', max: number, e: KeyboardEvent): void {
    const step: Record<string, number> = {
      ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1, PageUp: 5, PageDown: -5,
    };
    if (e.key === 'Home') this.setValue(key, 0, max);
    else if (e.key === 'End') this.setValue(key, max, max);
    else if (step[e.key] !== undefined) this.setValue(key, this[key] + step[e.key], max);
    else return;
    e.preventDefault();
  }

  calculate(): void {
    const r = this.live;
    if (!r) return;
    this.result = r;
    this.showResult = true;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  edit(): void {
    this.showResult = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  scale(score: number): number {
    return scalePercent(score);
  }

  cutoffOf(s: SchoolPrediction): number | null {
    return s.insideCutoff ?? s.outsideCutoff;
  }

  cutoffText(s: SchoolPrediction): string {
    return s.insideCutoff !== null
      ? `${s.insideCutoff} inside · ${s.outsideCutoff} outside`
      : `${s.outsideCutoff} outside only`;
  }

  gapText(diff: number | null): string {
    if (diff === null) return 'Cutoff not published';
    if (diff >= 0) return `+${diff} above cutoff`;
    if (diff === -1) return '1 point to go';
    return `${-diff} points to go`;
  }

  trackSchool(_: number, s: SchoolPrediction): string {
    return s.school;
  }
}
